<script setup lang="ts">
	import { MzToaster } from '@monizze/monizze-components';
	import { useI18n } from 'vue-i18n';
	import { useAuthStore } from '@/stores/auth.ts';
	import { detectedLanguage } from '@/main.ts';
	import { useStorage } from '@vueuse/core';
	import { onMounted } from 'vue';
	import { useUserStore } from '@/stores/user.ts';
	import { useHead } from '@unhead/vue';

	const { fallbackLocale, locale, t } = useI18n();
	const authStore = useAuthStore();
	const userStore = useUserStore();

	useHead({
		title: t('head.title'),
	});

	const updateLocale = (newLocale: string): void => {
		locale.value = newLocale;
		if (authStore.isLux) {
			fallbackLocale.value = newLocale.substring(0, 2);
		}
	};

	// `detectedLanguage` is a base code ("en"), while the registered locales — and therefore
	// `availableLanguages` — carry the `_LU` suffix on Lux. Suffix before comparing, otherwise the check
	// never matches on Lux and every visitor is pinned to `defaultLocale` (French).
	const detectedLocale = authStore.isLux ? `${detectedLanguage}_LU` : detectedLanguage;
	const selectedLanguage = authStore.availableLanguages.includes(detectedLocale) ? detectedLocale : authStore.defaultLocale;
	locale.value = selectedLanguage;

	onMounted(() => {
		if (!authStore.token) {
			return;
		}

		try {
			const account = useStorage('account', '{}');
			const parsedData = JSON.parse(account.value);
			if (parsedData && typeof parsedData === 'object' && parsedData.user) {
				userStore.user = parsedData.user;
			} else if (authStore.isLogged) {
				userStore.refreshUser().then(() => {
					updateLocale(authStore.availableLanguages.includes(userStore.user.language) ? userStore.user.language : authStore.defaultLocale);
				});
			}
		} catch {
			console.info('No local storage detected. Login required');
		}
	});
</script>

<template>
	<router-view />
	<mz-toaster class="top-22 md:top-2" />
</template>
